import { ChangeEvent, useMemo, useRef } from 'react';

import { FilePickerStatus } from '@/enums';
import {
  getInputAcceptFromPickerTypes,
  openUploadPicker
} from '@/helpers';
import { FilePickerType } from '@/types';

type UseUploadFilePickerOptions = {
  pickerTypes: FilePickerType[];
  multiple?: boolean;
  disabled?: boolean;
  onFilesSelected: (files: File[]) => Promise<unknown> | unknown;
};

export const useUploadFilePicker = ({
  pickerTypes,
  multiple = true,
  disabled = false,
  onFilesSelected
}: UseUploadFilePickerOptions) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const inputAccept = useMemo(() => {
    return getInputAcceptFromPickerTypes(pickerTypes);
  }, [pickerTypes]);

  const handleInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      await onFilesSelected(Array.from(files));
    }

    event.target.value = '';
  };

  const openFilePicker = async () => {
    if (disabled) {
      return;
    }

    const result = await openUploadPicker({
      pickerTypes,
      multiple
    });

    if (result === FilePickerStatus.UNSUPPORTED) {
      fileInputRef.current?.click();
      return;
    }

    if (result === FilePickerStatus.CANCELLED) {
      return;
    }

    if (result.length > 0) {
      await onFilesSelected(result);
    }
  };

  const hiddenInputProps = {
    ref: fileInputRef,
    type: 'file' as const,
    style: { display: 'none' },
    multiple,
    accept: inputAccept,
    disabled,
    onChange: handleInputChange
  };

  return {
    openFilePicker,
    hiddenInputProps
  };
};
